import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import PremiumCostGraph from '../components/PremiumCostGraph';
import SavingsComparisonCard from '../components/SavingsComparisonCard';
import ShadowCostGraph from '../components/ShadowCostGraph';
import ClusterScoreCard from '../components/ClusterScoreCard';
import ClusterScoreBadge from '../components/ClusterScoreBadge';
import { useCluster } from '../context/ClusterContext';
import { useSavings } from '../context/SavingsContext';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

import { parseUtc } from '../lib/parseUtc';
// Cache key for stats so navigating away and back shows the previous
// values instantly. Per-cluster keyed; cleared if the user logs out
// (sessionStorage is bound to the tab).
const STATS_CACHE_KEY = (cid) => `optops:dashboard:stats:${cid || 'all'}`;
const SHADOW_CACHE_KEY = (cid) => `optops:dashboard:shadow:${cid || 'all'}`;

function readCache(key) {
  try {
    const v = sessionStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or disabled — degrade silently */
  }
}

function Dashboard() {
  const { clusters, selectedCluster, loading: clustersLoading } = useCluster();
  // Per-fetcher seq counters guarding against stale-response races on
  // rapid cluster switch. CRITICAL: must be per-endpoint, not shared,
  // because the [selectedCluster] effect fires THREE parallel fetches
  // (stats / shadow / optimizations). A single shared counter would
  // have each later-starting fetch bump the seq, causing every
  // earlier-but-still-in-flight fetcher to fail its own seq-check on
  // response — net effect: only the LAST fetcher wins, the other two
  // silently `return` without updating state, and tiles stay empty.
  // That was the regression behind the customer's "dashboard breaking,
  // tiles not loading" report on a 2-day-old cluster.
  const statsSeqRef = useRef(0);
  const shadowSeqRef = useRef(0);
  const optsSeqRef = useRef(0);
  const metricsSeqRef = useRef(0);
  // Seed from sessionStorage so re-entry shows last-known values
  // instantly. Background refetch fills in fresh data without ever
  // hiding the page behind a skeleton again.
  const [stats, setStats] = useState(() =>
    readCache(STATS_CACHE_KEY(selectedCluster?.id))
  );
  const [shadowData, setShadowData] = useState(() =>
    readCache(SHADOW_CACHE_KEY(selectedCluster?.id))
  );
  const [recentMetrics, setRecentMetrics] = useState([]);
  // Time range driven by the header dropdown (Layout.js). The header
  // dispatches `monitoring-time-range-change` with `{ hours: <number> }`;
  // we map that to the string keys used by getMetricsLimit below.
  // Default = 24h so the first cluster fetch isn't a 1h-narrow window.
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedHours, setSelectedHours] = useState(24);

  useEffect(() => {
    const handler = (e) => {
      const hrs = Number(e?.detail?.hours);
      if (!Number.isFinite(hrs) || hrs <= 0) return;
      setSelectedHours(hrs);
      // Map to legacy string keys for getMetricsLimit. Anything unmapped
      // falls back to '24h' (= 24 sample points).
      const map = { 1: '1h', 3: '3h', 6: '6h', 12: '12h', 24: '24h', 48: '2d', 168: '7d', 720: '30d' };
      setSelectedTimeRange(map[hrs] || '24h');
    };
    window.addEventListener('monitoring-time-range-change', handler);
    return () => window.removeEventListener('monitoring-time-range-change', handler);
  }, []);
  const [clusterAgentEnabled, setClusterAgentEnabled] = useState(false);
  const [workloadAgentEnabled, setWorkloadAgentEnabled] = useState(false);
  const [latestMetric, setLatestMetric] = useState(null);
  const [recentOptimizations, setRecentOptimizations] = useState([]);
  const { realizedSavings, potentialSavings, savedTillNow, currentMonthCost, optimizationRate, savingsBreakdown } = useSavings();
  const navigate = useNavigate();

  // Staged "we can save ~$X/mo" breakdown. Per selected cluster use its own
  // stats; in the aggregate view use the context (which fetches the un-filtered
  // /dashboard/stats). Falls back to the legacy single number when absent.
  const breakdown = selectedCluster ? stats?.savings_breakdown : (savingsBreakdown || stats?.savings_breakdown);
  const fmtUsd = (v) => `$${Math.round(v || 0).toLocaleString()}`;

  const displayClusters = stats?.total_clusters || 0;
  const displaySavings = Math.round(
    selectedCluster
      ? (stats?.potential_savings || 0)
      : (potentialSavings || stats?.potential_savings || 0)
  );
  // All savings derived from shadow engine (month-to-date actuals)
  // When no cluster selected (aggregate view), fall back to API stats
  const displayRealizedSavings = Math.round(
    selectedCluster
      ? (shadowData?.summary?.total_savings || 0)
      : (realizedSavings || stats?.realized_savings || 0)
  );
  const displayCost = Math.round(shadowData?.summary?.total_actual || stats?.current_month_cost || 0);
  const displayBaselineCost = Math.round(
    selectedCluster
      ? (shadowData?.summary?.total_shadow || 0)
      : (stats?.baseline_cost || 0)
  );
  const displaySavingsPercent = selectedCluster
    ? (shadowData?.summary?.avg_savings_pct || 0)
    : (stats?.baseline_cost > 0
        ? Math.round(((stats?.realized_savings || 0) / stats.baseline_cost) * 1000) / 10
        : 0);
  const displayStorageCost = Math.round(stats?.total_storage_cost || 0);
  const displaySavedTillNow = Math.round(
    selectedCluster
      ? (stats?.saved_till_now || 0)
      : (savedTillNow || stats?.saved_till_now || 0)
  );

  // Fetch shadow cluster simulation data (with vs without OptOps) — month-to-date
  const fetchShadowCost = async (clusterId) => {
    const seq = ++shadowSeqRef.current;
    try {
      const mtdDays = new Date().getDate(); // days elapsed this month
      const response = await axios.get(`${API}/clusters/${clusterId}/shadow-cost?days=${mtdDays}`);
      if (seq !== shadowSeqRef.current) return; // newer shadow-cost fetch started; discard
      if (response?.data) {
        setShadowData(response.data);
        writeCache(SHADOW_CACHE_KEY(clusterId), response.data);
      }
    } catch (error) {
      console.error('Failed to fetch shadow cost:', error);
    }
  };

  // Fetch cluster-specific metrics when time range or cluster changes.
  // The "all clusters" branch is handled by the [selectedCluster] effect
  // below — we deliberately drop the legacy mount-only fetch that
  // fired in parallel and doubled the load on /dashboard/stats.
  useEffect(() => {
    if (selectedCluster) {
      fetchMetricsForCluster(selectedCluster);
    }
  }, [selectedTimeRange, selectedCluster]);

  // Fetch other cluster data when cluster changes.
  //
  // Policy: keep last-known stats/shadowData on screen during the brief
  // fetch window. Originally this was reverted to "clear on cache miss"
  // to avoid the stale-cluster-label issue — but that caused EVERY
  // first-paint to show empty $0 state instead of the loading skeleton,
  // which read to customers as "dashboard broken". The seq-guard plus
  // the per-cluster cache key already make the wrong-cluster-data
  // window tiny (<1s); preferring brief stale > confident-but-empty.
  useEffect(() => {
    const cid = selectedCluster?.id;
    const cachedStats = readCache(STATS_CACHE_KEY(cid));
    const cachedShadow = readCache(SHADOW_CACHE_KEY(cid));
    if (cachedStats) setStats(cachedStats);
    if (cachedShadow) setShadowData(cachedShadow);
    // Per-cluster live data — safe to drop; PieCharts show 0% briefly
    // until metrics fetch returns, which is the correct empty state.
    setLatestMetric(null);
    if (selectedCluster) {
      fetchStatsForCluster(selectedCluster);
      fetchRecentOptimizations(selectedCluster.id);
      fetchShadowCost(selectedCluster.id);
    } else {
      fetchInitialStats();
    }
  }, [selectedCluster]);

  const getMetricsLimit = (timeRange) => {
    switch (timeRange) {
      case '1h': return 12;
      case '6h': return 24;
      case '24h': return 24;
      case '7d': return 42;
      case '30d': return 60;
      default: return 24;
    }
  };

  const fetchRecentOptimizations = async (clusterId) => {
    const seq = ++optsSeqRef.current;
    try {
      const response = await axios.get(`${API}/clusters/${clusterId}/optimization-history?limit=5`);
      if (seq !== optsSeqRef.current) return; // newer optimization-history fetch started; discard
      // Optional chaining: if backend returns null body or `{}`, accessing
      // `.history` direct would throw and break the .then chain — leaving
      // the Recent Optimizations panel stuck on its initial empty state
      // without any operator signal.
      setRecentOptimizations(response?.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch optimization history:', error);
      // Fallback to empty array
      setRecentOptimizations([]);
    }
  };

  const fetchMetricsForCluster = async (cluster) => {
    const seq = ++metricsSeqRef.current;
    try {
      const limit = getMetricsLimit(selectedTimeRange);
      // Pass `hours` so the backend filters to the selected window (Layout
      // header dropdown). Backend signature: GET /agent-metrics/{id}?limit=N&hours=H
      const qs = `?limit=${limit}&hours=${selectedHours}`;
      const metricsEndpoint = cluster.connection_mode === 'agent'
        ? `${API}/agent-metrics/${cluster.id}${qs}`
        : `${API}/metrics/${cluster.id}${qs}`;
      const metricsRes = await axios.get(metricsEndpoint);
      if (seq !== metricsSeqRef.current) return; // newer metrics fetch (cluster/time-range change) started; discard
      const historicalMetrics = (metricsRes?.data || []).slice(0, limit).reverse();
      setRecentMetrics(historicalMetrics);
      // Set the latest metric for CPU/Memory display
      if (historicalMetrics.length > 0) {
        setLatestMetric(historicalMetrics[historicalMetrics.length - 1]);
      }
    } catch (error) {
      console.error('Failed to fetch metrics for cluster:', error);
    }
  };

  const fetchStatsForCluster = async (cluster) => {
    const seq = ++statsSeqRef.current;
    try {
      const statsRes = await axios.get(`${API}/dashboard/stats?cluster_id=${cluster.id}`);
      if (seq !== statsSeqRef.current) return; // newer stats fetch started; discard
      if (statsRes?.data) {
        setStats(statsRes.data);
        writeCache(STATS_CACHE_KEY(cluster.id), statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats for cluster:', error);
    }
  };

  const fetchInitialStats = async () => {
    const seq = ++statsSeqRef.current;
    try {
      if (selectedCluster) return;
      const statsRes = await axios.get(`${API}/dashboard/stats`);
      if (seq !== statsSeqRef.current) return; // newer stats fetch started; discard
      if (statsRes?.data) {
        setStats(statsRes.data);
        writeCache(STATS_CACHE_KEY(null), statsRes.data);
      }
    } catch (error) {
      // Suppress the toast when we already have cached data on screen — the
      // user would see "Failed to load" while looking at perfectly fine
      // (cached) numbers, which reads as broken UI. Log silently; the
      // background poll will retry.
      console.error('Failed to load dashboard data:', error);
      if (!stats) {
        toast.error('Failed to load dashboard data');
      }
    }
  };

  // Page always renders the layout instantly; per-card placeholders
  // (zero values from null-coalesced display* vars) cover the brief
  // window while the background fetches complete. On revisit the
  // sessionStorage cache makes the values appear immediately.

  // No clusters connected yet → show a focused onboarding panel
  // instead of zeroed-out stat cards. We MUST wait for the initial
  // cluster fetch to finish — otherwise the cluster list is still its
  // useState([]) seed and we flash the "Connect your first cluster"
  // banner for a fraction of a second to every returning user before
  // their real clusters appear. Customer-visible flicker on every page
  // load; reported as "the dashboard flashed onboarding then loaded".
  if (!clustersLoading && clusters && clusters.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="max-w-lg w-full text-center px-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 mb-6">
              <span className="material-symbols-outlined text-[28px] text-zinc-700 dark:text-zinc-300">cloud_sync</span>
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-white">
              Connect your first cluster
            </h2>
            <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
              OptOps needs a Kubernetes cluster to start surfacing savings, rightsizing
              recommendations, and node-optimization actions. The agent installs with a
              single Helm command and connects in under 30 seconds.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <button
                onClick={() => navigate('/clusters')}
                className="px-5 py-2.5 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
              >
                Add cluster
              </button>
              <a
                href="/docs/#/install"
                className="px-5 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                Install docs
              </a>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Optimization Opportunity Alert — staged "we can save ~$X/mo" headline */}
        <div className="rounded-xl bg-gradient-to-r from-primary/20 to-transparent border border-primary/20 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-primary/20 text-primary">
              <span className="material-symbols-outlined text-[24px]">savings</span>
            </div>
            {breakdown && breakdown.confidence === 'no_data' ? (
              <div>
                <h3 className="text-sm font-semibold dark:text-white text-slate-900">Reduce Your Cloud Bill</h3>
                <p className="text-sm text-text-secondary">Agent is still collecting data — your savings estimate will appear shortly.</p>
              </div>
            ) : breakdown && (breakdown.approx_total_monthly > 0) ? (
              <div>
                <h3 className="text-sm font-semibold dark:text-white text-slate-900">Reduce Your Cloud Bill</h3>
                <p className="text-sm text-text-secondary">
                  We can save you <span className="text-primary font-bold">~{fmtUsd(breakdown.approx_total_monthly)}/month more</span>
                  <span className="text-text-secondary" title="Additional opportunity on top of what OptOps already saves you. Consolidation + Spot/instance-type are the components; Rightsizing is the enabler, not added to the total.">　ⓘ</span>
                  {breakdown.realized_monthly > 0 && (
                    <span className="text-text-secondary"> · already saving <b className="text-slate-700 dark:text-slate-200">{fmtUsd(breakdown.realized_monthly)}/mo</b></span>
                  )}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-secondary">
                  <span>Consolidation <b className="text-slate-700 dark:text-slate-200">{fmtUsd(breakdown.categories?.consolidation?.monthly)}</b></span>
                  <span>Spot / instance-type <b className="text-slate-700 dark:text-slate-200">{fmtUsd(breakdown.categories?.instance_optimization?.monthly)}</b></span>
                  <span>Rightsizing enables this <b className="text-slate-700 dark:text-slate-200">{fmtUsd(breakdown.categories?.rightsizing_enabler?.request_priced_monthly)}</b></span>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-sm font-semibold dark:text-white text-slate-900">Reduce Your Cloud Bill</h3>
                <p className="text-sm text-text-secondary">
                  Actionable rightsizing recommendations found. You could be saving <span className="text-primary font-bold">${displaySavings}/month</span> instantly.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            {/* Cluster Score surfaced prominently in the header for the
                selected cluster. The full breakdown card still renders in
                the stats grid below; this keeps the headline KPI visible
                at the top of the page (and reachable even after scrolling). */}
            {selectedCluster?.id && <ClusterScoreBadge clusterId={selectedCluster.id} />}
            <button
              onClick={() => navigate(breakdown && breakdown.approx_total_monthly > 0 ? '/realizable-savings' : '/rightsizing')}
              className="whitespace-nowrap px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors shadow-lg shadow-primary/20 animate-pulse"
            >
              {breakdown && breakdown.approx_total_monthly > 0 ? 'See How' : 'Unlock Savings Now'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Cluster Score (W7) — per-cluster only; the score endpoint has
              no aggregate form, so the card hides in the all-clusters view. */}
          {selectedCluster?.id && <ClusterScoreCard clusterId={selectedCluster.id} />}

          {/* Total Spend */}
          {/* <div */}
          {/*   id="tour-total-spend" */}
          {/*   className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all group" */}
          {/* > */}
          {/*   <div className="flex items-center justify-between"> */}
          {/*     <div className="flex items-center gap-1.5"> */}
          {/*       <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Total Spend</p> */}
          {/*       <UITooltip> */}
          {/*         <TooltipTrigger asChild> */}
          {/*           <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" /> */}
          {/*         </TooltipTrigger> */}
          {/*         <TooltipContent side="top"> */}
          {/*           <p className="w-64">Total cloud expenditure across all connected Kubernetes clusters for the current month.</p> */}
          {/*         </TooltipContent> */}
          {/*       </UITooltip> */}
          {/*     </div> */}
          {/*     <span className="material-symbols-outlined text-slate-400 dark:text-slate-600 text-[20px]">payments</span> */}
          {/*   </div> */}
          {/*   <div className="flex items-end gap-2"> */}
          {/*     <h3 className="text-2xl font-bold dark:text-white text-slate-900">${displayCost.toLocaleString()}</h3> */}
          {/*     <span className="text-xs font-medium text-text-secondary mb-1">/ month</span> */}
          {/*   </div> */}
          {/*   {displayBaselineCost > displayCost && ( */}
          {/*     <div className="flex items-center gap-1 text-success text-xs font-medium"> */}
          {/*       <span className="material-symbols-outlined text-[16px]">trending_down</span> */}
          {/*       <span>Was ${displayBaselineCost.toLocaleString()} without OptOps</span> */}
          {/*     </div> */}
          {/*   )} */}
          {/*   {displayBaselineCost <= displayCost && ( */}
          {/*     <div className="flex items-center gap-1 text-text-secondary text-xs font-medium"> */}
          {/*       <span className="material-symbols-outlined text-[16px]">schedule</span> */}
          {/*       <span>Tracking cost history...</span> */}
          {/*     </div> */}
          {/*   )} */}
          {/* </div> */}

          {/* Realized Savings */}
          <div
            id="tour-realized-savings"
            className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm relative overflow-hidden group hover:shadow-md transition-all"
          >
            <div className="absolute inset-0 bg-success/5 dark:bg-success/5 pointer-events-none"></div>
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-1.5">
                <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Realized Savings</p>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="w-64">Actual savings achieved by applying OptOps AI recommendations and node optimizations.</p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <span className="material-symbols-outlined text-success/70 text-[20px]">verified</span>
            </div>
            <div className="flex items-end gap-2 relative z-10">
              <h3 className="text-2xl font-bold text-success dark:text-success-light">${displayRealizedSavings.toLocaleString()}</h3>
              <span className="text-xs font-medium text-text-secondary mb-1">/ mtd</span>
            </div>
            <div className="flex items-center gap-1 text-success text-xs font-medium relative z-10">
              <span className="material-symbols-outlined text-[16px]">trending_up</span>
              <span>{displaySavingsPercent > 0 ? `${displaySavingsPercent}% saved vs baseline` : 'Accumulating data...'}</span>
            </div>
          </div>

          {/* Efficiency Score */}
          <div
            id="tour-optimization-rate"
            className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all relative overflow-hidden"
          >
            <div className="flex items-center justify-between">
              <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Efficiency Score</p>
              <span className="material-symbols-outlined text-slate-400 dark:text-slate-600 text-[20px]">speed</span>
            </div>
            {optimizationRate == null ? (
              // No idle/usage data yet — show an honest placeholder instead of
              // a score computed on absent data (would otherwise read 100%).
              <>
                <div className="flex items-end gap-2 text-slate-400 dark:text-slate-500">
                  <h3 className="text-2xl font-bold">—</h3>
                </div>
                <div className="flex items-center gap-1 text-xs font-medium text-slate-400 dark:text-slate-500">
                  <span className="material-symbols-outlined text-[16px]">hourglass_empty</span>
                  <span>Insufficient usage data</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-end gap-2 text-slate-900 dark:text-white">
                  <h3 className="text-2xl font-bold">{optimizationRate}%</h3>
                  <div className="mb-1.5 w-full max-w-[60px]">
                    <div className="h-1.5 w-full bg-slate-100 dark:bg-surface-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${optimizationRate}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium ${
                  optimizationRate >= 75 ? 'text-success' :
                  optimizationRate >= 50 ? 'text-blue-500' :
                  optimizationRate >= 25 ? 'text-amber-500' :
                  'text-red-500'
                }`}>
                  <span className="material-symbols-outlined text-[16px]">{
                    optimizationRate >= 75 ? 'check_circle' :
                    optimizationRate >= 50 ? 'info' :
                    optimizationRate >= 25 ? 'warning' :
                    'error'
                  }</span>
                  <span>{
                    optimizationRate >= 75 ? 'Highly optimized' :
                    optimizationRate >= 50 ? 'Moderately optimized' :
                    optimizationRate >= 25 ? 'Needs improvement' :
                    'Under-optimized'
                  }</span>
                </div>
              </>
            )}
          </div>

          {/* Potential Savings */}
          {/* <div */}
          {/*   id="tour-potential-savings" */}
          {/*   onClick={() => navigate('/rightsizing')} */}
          {/*   className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all cursor-pointer group hover:border-blue-400 dark:hover:border-blue-500/50" */}
          {/* > */}
          {/*   <div className="flex items-center justify-between"> */}
          {/*     <div className="flex items-center gap-1.5"> */}
          {/*       <p className="text-slate-500 dark:text-text-secondary text-sm font-medium text-slate-500 dark:text-text-secondary">Potential Savings</p> */}
          {/*       <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Unclaimed</span> */}
          {/*     </div> */}
          {/*     <span className="material-symbols-outlined text-slate-400 dark:text-slate-600 text-[20px] transition-transform group-hover:scale-110">account_balance_wallet</span> */}
          {/*   </div> */}
          {/*   <div className="flex items-end gap-2"> */}
          {/*     <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 transition-colors group-hover:text-blue-700 font-bold">${displaySavings.toLocaleString()}</h3> */}
          {/*     <span className="text-xs font-medium text-text-secondary mb-1">/ month</span> */}
          {/*   </div> */}
          {/*   <div className="mt-auto text-left text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform"> */}
          {/*     Claim Savings Now */}
          {/*     <span className="material-symbols-outlined text-[14px]">arrow_forward</span> */}
          {/*   </div> */}
          {/* </div> */}

          {/* Saved Till Now */}
          <div
            id="tour-saved-till-now"
            className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm relative overflow-hidden group hover:shadow-md transition-all"
          >
            <div className="absolute inset-0 bg-emerald-500/5 dark:bg-emerald-500/5 pointer-events-none"></div>
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-1.5">
                <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Saved Till Now</p>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="w-64">Cumulative savings across all months since OptOps was enabled. Realized savings reset monthly, but this total keeps accumulating.</p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <span className="material-symbols-outlined text-emerald-500/70 text-[20px]">savings</span>
            </div>
            <div className="flex items-end gap-2 relative z-10">
              <h3 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">${displaySavedTillNow.toLocaleString()}</h3>
              <span className="text-xs font-medium text-text-secondary mb-1">total</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-medium relative z-10">
              <span className="material-symbols-outlined text-[16px]">check_circle</span>
              <span>Lifetime savings with OptOps</span>
            </div>
          </div>

          {/* Idle Cost card removed from the dashboard per request. */}

          {/* Storage Cost */}
          <div
            id="tour-storage-cost"
            className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 flex flex-col gap-3 shadow-sm relative overflow-hidden group hover:shadow-md transition-all"
          >
            <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-500/5 pointer-events-none"></div>
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-1.5">
                <p className="text-slate-500 dark:text-text-secondary text-sm font-medium">Storage Cost</p>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="w-64">Monthly cost of PersistentVolumes attached to your clusters — EBS, GCP Persistent Disks, Azure Managed Disks.</p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <span className="material-symbols-outlined text-blue-500/70 text-[20px]">hard_drive</span>
            </div>
            <div className="flex items-end gap-2 relative z-10">
              <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">${displayStorageCost.toLocaleString()}</h3>
              <span className="text-xs font-medium text-text-secondary mb-1">/ month</span>
            </div>
            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium relative z-10">
              <span className="material-symbols-outlined text-[16px]">database</span>
              <span>{displayStorageCost > 0 ? 'Persistent volume costs' : 'No volumes detected'}</span>
            </div>
          </div>
        </div>

        {/* Savings Comparison Section - Single Source of Truth.
            `fallbackMonthlyCost` is the real month-to-date cost from
            /dashboard/stats. For new clusters the shadow simulation
            hasn't accumulated yet (shadowData.summary.* = 0) but the
            actual cost IS real — without this fallback the card shows
            "$0 vs $0" which reads as broken. */}
        <SavingsComparisonCard
          clusterId={selectedCluster?.id}
          shadowData={shadowData}
          fallbackMonthlyCost={Number(stats?.current_month_cost) || 0}
        />

        {/* Shadow Cluster: With vs Without OptOps */}
        {selectedCluster?.id && (
          <ShadowCostGraph clusterId={selectedCluster.id} />
        )}

        {/* Resource Usage */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CPU Usage - Pie Chart */}
            <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 shadow-sm flex-1">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900">CPU Usage</h4>
                  <p className="text-xs text-text-secondary">Cluster resource allocation</p>
                </div>
                <div className="bg-primary/10 text-primary p-1.5 rounded-lg">
                  <span className="material-symbols-outlined text-[20px]">memory</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Used', value: latestMetric?.cpu_usage || 0 },
                          { name: 'Idle', value: 100 - (latestMetric?.cpu_usage || 0) }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={22}
                        outerRadius={36}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                      >
                        <Cell fill="#2b8cee" />
                        <Cell fill="#e5e7eb" className="dark:fill-surface-border" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-primary"></span>
                    <span className="text-slate-500 dark:text-text-secondary">Used: <span className="dark:text-white text-slate-900 font-semibold">{(latestMetric?.cpu_usage || 0).toFixed(1)}%</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-surface-border"></span>
                    <span className="text-slate-500 dark:text-text-secondary">Idle: <span className="dark:text-white text-slate-900 font-semibold">{(100 - (latestMetric?.cpu_usage || 0)).toFixed(1)}%</span></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Memory Usage - Pie Chart */}
            <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 shadow-sm flex-1">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900">Memory Usage</h4>
                  <p className="text-xs text-text-secondary">RAM allocation status</p>
                </div>
                <div className="bg-purple-500/10 text-purple-500 p-1.5 rounded-lg">
                  <span className="material-symbols-outlined text-[20px]">database</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Used', value: latestMetric?.memory_usage || 0 },
                          { name: 'Available', value: 100 - (latestMetric?.memory_usage || 0) }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={22}
                        outerRadius={36}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                      >
                        <Cell fill="#a855f7" />
                        <Cell fill="#e5e7eb" className="dark:fill-surface-border" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                    <span className="text-slate-500 dark:text-text-secondary">Used: <span className="dark:text-white text-slate-900 font-semibold">{(latestMetric?.memory_usage || 0).toFixed(1)}%</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-surface-border"></span>
                    <span className="text-slate-500 dark:text-text-secondary">Available: <span className="dark:text-white text-slate-900 font-semibold">{(100 - (latestMetric?.memory_usage || 0)).toFixed(1)}%</span></span>
                  </div>
                </div>
              </div>
            </div>
        </div>

        {/* Cluster Status & Recent Optimizations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cluster Status Table */}
          <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl overflow-hidden shadow-sm flex flex-col">
            <div className="p-5 border-b border-gray-200 dark:border-surface-border flex justify-between items-center">
              <h3 className="text-base font-semibold dark:text-white text-slate-900">Cluster Status</h3>
              <a href="/clusters" className="text-primary hover:text-primary-hover text-sm font-medium">View All</a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[480px]">
                <thead>
                  <tr className="bg-gray-50 dark:bg-background-dark/50 text-xs font-semibold text-slate-500 dark:text-text-secondary uppercase tracking-wider">
                    <th className="px-3 sm:px-5 py-3">Cluster Name</th>
                    <th className="px-3 sm:px-5 py-3">Region</th>
                    <th className="px-3 sm:px-5 py-3">Nodes</th>
                    <th className="px-3 sm:px-5 py-3 text-right">Cost/Hr</th>
                    <th className="px-3 sm:px-5 py-3 text-center">Health</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-200 dark:divide-surface-border">
                  {clusters.slice(0, 3).map((cluster) => (
                    <tr key={cluster.id} className="hover:bg-gray-50 dark:hover:bg-background-dark/30 transition-colors">
                      <td className="px-3 sm:px-5 py-3 sm:py-4 font-medium dark:text-white text-slate-900 truncate max-w-[140px]">{cluster.name}</td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-text-secondary">{cluster.region || 'N/A'}</td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-text-secondary">{cluster.node_count || 'N/A'}</td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-right dark:text-white text-slate-900 font-medium">
                        ${((cluster.hourly_cost || 0)).toFixed(2)}
                      </td>
                      <td className="px-3 sm:px-5 py-3 sm:py-4 text-center">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${cluster.status === 'healthy' ? 'bg-success' : 'bg-yellow-500'}`} />
                      </td>
                    </tr>
                  ))}
                  {clusters.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-8 text-center text-text-secondary">
                        No clusters connected
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Optimizations */}
          <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-5 shadow-sm flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold dark:text-white text-slate-900">Recent Optimizations</h3>
              <button
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-surface-border text-slate-500"
                onClick={() => selectedCluster && fetchRecentOptimizations(selectedCluster.id)}
              >
                <span className="material-symbols-outlined text-[20px]">refresh</span>
              </button>
            </div>
            <div className="flex flex-col gap-4">
              {recentOptimizations.length > 0 ? (
                recentOptimizations.map((opt, index) => (
                  <OptimizationItem
                    key={opt.id || index}
                    icon={getOptimizationIcon(opt.action_type)}
                    iconBg={getOptimizationIconBg(opt.action_type)}
                    iconColor={getOptimizationIconColor(opt.action_type)}
                    title={formatOptimizationTitle(opt)}
                    time={formatTimeAgo(opt.timestamp || opt.created_at)}
                    description={formatOptimizationDescription(opt)}
                    savings={`$${(opt.estimated_savings || opt.actual_savings || 0).toFixed(2)}`}
                    status={opt.status}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-text-secondary">
                  <span className="material-symbols-outlined text-4xl mb-2 opacity-50">auto_fix_high</span>
                  <p className="text-sm">No recent optimizations</p>
                  <p className="text-xs mt-1">Optimizations will appear here when applied</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function OptimizationItem({ icon, iconBg, iconColor, title, time, description, savings, status }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`mt-0.5 min-w-[32px] h-8 rounded-full ${iconBg} flex items-center justify-center ${iconColor}`}>
        <span className="material-symbols-outlined text-[16px]">{icon}</span>
      </div>
      <div className="flex flex-1 flex-col pb-4 border-b border-dashed border-gray-200 dark:border-surface-border last:border-0 last:pb-0 min-w-0">
        <div className="flex flex-wrap justify-between items-center gap-1">
          <p className="text-sm font-medium dark:text-white text-slate-900 truncate">{title}</p>
          <div className="flex items-center gap-2 shrink-0">
            {status && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
                  status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400' :
                    'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400'
                }`}>
                {status}
              </span>
            )}
            <span className="text-xs text-text-secondary whitespace-nowrap">{time}</span>
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-1">{description}</p>
        {savings && savings !== '$0.00' && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-success">
            <span className="material-symbols-outlined text-[14px]">arrow_downward</span> {savings} / mo savings
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions for optimization display
function getOptimizationIcon(actionType) {
  switch (actionType) {
    case 'rightsizing':
    case 'rightsize':
      return 'tune';
    case 'bin_pack':
    case 'bin_packing':
      return 'compress';
    case 'scale_down':
      return 'trending_down';
    case 'scale_up':
      return 'trending_up';
    case 'spot_replacement':
      return 'swap_horiz';
    case 'delete':
    case 'cleanup':
      return 'delete';
    default:
      return 'bolt';
  }
}

function getOptimizationIconBg(actionType) {
  switch (actionType) {
    case 'rightsizing':
    case 'rightsize':
      return 'bg-primary/10';
    case 'bin_pack':
    case 'bin_packing':
      return 'bg-purple-500/10';
    case 'scale_down':
      return 'bg-green-500/10';
    case 'scale_up':
      return 'bg-orange-500/10';
    case 'spot_replacement':
      return 'bg-blue-500/10';
    default:
      return 'bg-primary/10';
  }
}

function getOptimizationIconColor(actionType) {
  switch (actionType) {
    case 'rightsizing':
    case 'rightsize':
      return 'text-primary';
    case 'bin_pack':
    case 'bin_packing':
      return 'text-purple-500';
    case 'scale_down':
      return 'text-green-500';
    case 'scale_up':
      return 'text-orange-500';
    case 'spot_replacement':
      return 'text-blue-500';
    default:
      return 'text-primary';
  }
}

function formatOptimizationTitle(opt) {
  const workloadName = opt.workload_name || opt.name || 'Unknown';

  switch (opt.action_type) {
    case 'rightsizing':
    case 'rightsize':
      return `Rightsized ${workloadName}`;
    case 'bin_pack':
    case 'bin_packing':
      return `Bin Packed ${workloadName}`;
    case 'scale_down':
      return `Scaled Down ${workloadName}`;
    case 'scale_up':
      return `Scaled Up ${workloadName}`;
    default:
      return `Optimized ${workloadName}`;
  }
}

function formatOptimizationDescription(opt) {
  const namespace = opt.namespace ? `in ${opt.namespace}` : '';

  if (opt.changes) {
    const changes = [];
    if (opt.changes.cpu_before && opt.changes.cpu_after) {
      changes.push(`CPU: ${opt.changes.cpu_before} → ${opt.changes.cpu_after}`);
    }
    if (opt.changes.memory_before && opt.changes.memory_after) {
      changes.push(`Memory: ${opt.changes.memory_before} → ${opt.changes.memory_after}`);
    }
    if (changes.length > 0) {
      return `${changes.join(', ')} ${namespace}`;
    }
  }

  if (opt.recommended_cpu || opt.recommended_memory) {
    return `Adjusted resources to CPU: ${opt.recommended_cpu || 'N/A'}, Memory: ${opt.recommended_memory || 'N/A'} ${namespace}`;
  }

  return `Applied optimization ${namespace}`.trim();
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  const date = parseUtc(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default Dashboard;
